import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import { evaluateHomePlanCreationEligibility } from "api-types/locations/homePlanCreationEligibility";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import type { ClientLot, ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { LotStatus } from "api-types/lots/lotTypes";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { isMasterPlanMultiUnit } from "lib/components/builders/communities/plans/planUtils";
import type { CommunityPlanLotInfo } from "./LotsTypes";

export const GARAGE_HANDING_MULTI_UNIT_TOOLTIP = "Garage handing not supported for lots in Multi-Unit locations";

// Location plans offered in the lot table "Plans Available" MultiSelect.
// Omits multi-unit plans when the location is not multi-unit.
export function buildLotTableCommunityPlanOptions({
  locationPlans,
  masterPlans,
  location,
}: {
  locationPlans: ClientLocationPlan[] | undefined;
  masterPlans: ClientMasterPlan[] | undefined;
  location: ClientLocationWithLevel | undefined;
}): CommunityPlanLotInfo[] {
  if (!locationPlans) {
    return [];
  }

  const masterPlansById = Object.fromEntries(masterPlans?.map((mp) => [mp.id, mp]) ?? []);
  const atMultiUnitLocation = isMultiUnitLocation(location);
  const options: CommunityPlanLotInfo[] = [];

  for (const plan of [...locationPlans].sort((a, b) => a.name.localeCompare(b.name))) {
    if (!plan.locationId || !plan.masterPlanId) {
      continue;
    }
    const masterPlan = masterPlansById[plan.masterPlanId];
    const eligibility = evaluateHomePlanCreationEligibility({
      atMultiUnitLocation,
      planIsMultiUnit: isMasterPlanMultiUnit(masterPlan),
    });
    if (!eligibility.ok) {
      continue;
    }
    options.push({
      value: plan.id ?? "",
      label: plan.name,
      masterPlanId: plan.masterPlanId,
      masterPlanName: masterPlan?.name ?? "",
    });
  }

  return options;
}

export function isLotInMultiUnitLocation(lot: Pick<ClientLotHydrated, "locationLevelType">): boolean {
  return lot.locationLevelType === "multiunit";
}

/**
 * Checks if a new home plan can be created on a given lot.
 * Only lots with status "available" and no active home plans are eligible.
 */
export function canCreateHomePlanOnLot(lot: ClientLotHydrated | null | undefined): boolean {
  if (!lot) {
    return false;
  }

  if (lot.lotStatus !== "available") {
    return false;
  }

  const hasActiveHomePlan = lot.configuredHomePlans?.some(
    (hp) => hp.planStatus === "PRE_CONSTRUCTION" || hp.planStatus === "CONSTRUCTION",
  );

  if (hasActiveHomePlan) {
    return false;
  }

  return true;
}

// Mirrors server-side validateLotIsMovable from lotService.ts
// Could consider trying to share between client and server, but kept separate for now
const IMMOVABLE_STATUSES: LotStatus[] = ["sold", "pending"];

/**
 * Everything eligibility looks at, mirroring the server's own `LotMoveCandidate`. Stated as counts
 * rather than the plans themselves, so callers can gate on the plan-count lot response.
 */
export type LotUnitAssignmentCandidate = Pick<ClientLot, "lotStatus" | "releasedAt" | "reportingUnitId"> & {
  homePlanCount: number;
  lotPlanCount: number;
};

export function getUnitAssignmentIneligibilityReason(lot: LotUnitAssignmentCandidate): string | null {
  if (lot.releasedAt) {
    return "released";
  }
  if (IMMOVABLE_STATUSES.includes(lot.lotStatus)) {
    return `status:${lot.lotStatus}`;
  }
  if (lot.reportingUnitId) {
    return "hasUnitAssignment";
  }
  if (lot.homePlanCount > 0) {
    return `hasHomePlans:${lot.homePlanCount}`;
  }
  if (lot.lotPlanCount > 0) {
    return `hasLotPlanAssignments:${lot.lotPlanCount}`;
  }
  return null;
}

export function isLotEligibleForUnitAssignment(lot: LotUnitAssignmentCandidate): boolean {
  return getUnitAssignmentIneligibilityReason(lot) === null;
}
